import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { Alert } from '../../models/api/alerts/alert';
import {
  AlertMessage,
  AlertMessageStatus,
} from '../../models/api/alerts/alert-message';
import { MessageCount } from '../../models/api/alerts/message-count';
import { Api } from '../api/api.service';
import { BaseService } from '../base.service';
import { Session } from '../session/session.service';

@Injectable({ providedIn: 'root' })
export class AlertsDataService extends BaseService {
  private api = inject(Api);
  private session = inject(Session);

  private _messageFetchCount = 1000;

  public get messageFetchCount(): number {
    return this._messageFetchCount;
  }

  private messages: AlertMessage[] = null;

  private alerts: Alert[] = null;

  public messagesChanged = new Subject<AlertMessage[]>();

  public alertsChanged = new Subject<Alert[]>();

  constructor() {
    super();
    this.dispose.subs = this.session.cacheCleared.subscribe(() => {
      this._messageFetchCount = 1000;
      this.messages = null;
      this.alerts = null;
      this.messagesChanged = new Subject<AlertMessage[]>();
      this.alertsChanged = new Subject<Alert[]>();
    });
  }

  async getMessages(refreshCache = false): Promise<AlertMessage[]> {
    if (!this.messages || refreshCache) {
      const response = await this.api.alertMessages.getMessages(
        this.messageFetchCount,
        {
          cache: false,
        },
      );
      this.messages = response.messages;
    }

    return this.messages;
  }

  async getAlerts(refreshCache = false) {
    if (!this.alerts || refreshCache) {
      this.alerts = await this.api.manageAlerts.getAllAlerts(refreshCache);
    }

    return this.alerts;
  }

  async deleteMessages(messages: AlertMessage[]) {
    const deleteAll = messages.length === this.messages.length;
    const messageIds = deleteAll ? [] : messages.map((m) => m.alertMessageId);
    await this.api.alertMessages.deleteMessages({ messageIds, deleteAll });
    this.messages = this.messages.filter((msg) => !messages.includes(msg));
    this.messagesChanged.next(this.messages);
  }

  async setStatus(messages: AlertMessage[], newStatus: AlertMessageStatus) {
    const messageIds =
      messages.length === this.messages.length
        ? []
        : messages.map((m) => m.alertMessageId);

    for (const message of messages) {
      message.messageStatus = newStatus;
    }

    this.messages = [...this.messages];
    this.messagesChanged.next(this.messages);

    await this.api.alertMessages.updateStatus({
      status: newStatus,
      messageIds: messageIds,
      applyToAll: !messageIds.length,
    });
  }

  async getMessageCounts(refreshCache = false): Promise<MessageCount[]> {
    return await this.api.manageAlerts.getMessageCounts(refreshCache);
  }

  async updateMessageFetchCount(count: number) {
    this._messageFetchCount = count;
    await this.getMessages(true);
    this.messagesChanged.next(this.messages);
  }

  async deleteAlert(alertId: number) {
    await this.api.manageAlerts.deleteAlert(alertId);

    await this.getAlerts();
    this.alerts = this.alerts.filter((x) => x.alertId !== alertId);
    this.alertsChanged.next(this.alerts);
  }

  async markAlertMessagesAsRead(alertId: number) {
    await this.api.alertMessages.markAlertMessagesAsRead(alertId);
    if (this.messages) {
      const messages = this.messages.filter((x) => x.alertId === alertId);
      for (const message of messages) {
        message.messageStatus = 'READ';
      }

      this.messagesChanged.next(this.messages);
    }
  }

  async saveAlert(alert: Alert): Promise<number> {
    const result = await this.api.manageAlerts.saveAlert(alert);

    await Promise.all([this.getAlerts(true), this.getMessageCounts(true)]);
    this.alertsChanged.next(this.alerts);

    // if we are updating an existing alert, refrsh messages since
    // name may have changed
    if (alert.alertId) {
      await this.getMessages(true);
      this.messagesChanged.next(this.messages);
    }

    return result.alertId;
  }
}
